import type { ReactElement } from "react";
import { KIT_METRICS, logical, type FlagSize } from "./metrics.ts";

/**
 * The PvP flag mark (brief section 6.5, Bible 6.1): the flag state as a shape in its colour (a shield for
 * Protected, a pennant for Flagged, a burst for Combatant), with the lock when an area holds the state, and
 * optionally its word. The mark is the atlas's own frame (`ui/flag/<shape>[-locked]_<size>`), drawn by its
 * generated `background-position` rule with no clip-path, so the DOM shows the same pixels the canvas draws
 * from the same frame. Its box always holds the locked variant, with the mark in its top left where that
 * variant draws it, so the lock appearing moves nothing beside it. It is display-only and takes no pointer
 * events.
 */

/**
 * A PvP flag state.
 */
export type FlagState = "protected" | "flagged" | "combatant";

/**
 * The shape each state's mark takes in the atlas.
 */
const SHAPES: Readonly<Record<FlagState, string>> = { protected: "shield", flagged: "pennant", combatant: "burst" };

/**
 * Each state's word (Bible 6.1).
 */
const WORDS: Readonly<Record<FlagState, string>> = { protected: "Protected", flagged: "Flagged", combatant: "Combatant" };

/**
 * The generated class that draws a flag mark.
 *
 * @param state The flag state.
 * @param locked Whether an area holds it, which draws the variant carrying the lock.
 * @param size The mark's size.
 * @returns `ot-ui-flag-<shape>[-locked]_<size>`.
 * @example
 * flagMarkClass("flagged", true, 16);
 * // "ot-ui-flag-pennant-locked_16"
 */
export function flagMarkClass(state: FlagState, locked: boolean, size: FlagSize): string {
  return `ot-ui-flag-${SHAPES[state]}${locked ? "-locked" : ""}_${size}`;
}

/**
 * A flag state's words as the HUD writes them: the state, with "(locked)" when an area holds it.
 *
 * @param state The flag state.
 * @param locked Whether an area holds it.
 * @returns The words.
 * @example
 * flagWords("flagged", true);
 * // "Flagged (locked)"
 */
export function flagWords(state: FlagState, locked: boolean): string {
  return locked ? `${WORDS[state]} (locked)` : WORDS[state];
}

/**
 * A flag mark.
 *
 * @param props The `state`; `locked` when an area holds it; its `size`, 16 in the HUD or 8 beside a name;
 *   and `showWord` to set its words after it in the pixel face, in the state's colour.
 * @returns The mark, an image named by its words when they are not shown.
 */
export function FlagMark({ state, locked = false, size, showWord = false }: { readonly state: FlagState; readonly locked?: boolean; readonly size: FlagSize; readonly showWord?: boolean }): ReactElement {
  const row = KIT_METRICS.flag[size];
  const words = flagWords(state, locked);
  const box = (
    <span className="ot-flag__box" data-kit="flag-box" aria-hidden={showWord ? true : undefined} style={{ width: logical(row.box), height: logical(row.box), marginTop: showWord ? logical(row.markTop) : undefined }}>
      <span className={`ot-flag__mark ${flagMarkClass(state, locked, size)}`} data-kit="flag-mark" />
    </span>
  );
  if (!showWord) {
    return (
      <span className="ot-flag" role="img" aria-label={words} data-kit="flag">
        {box}
      </span>
    );
  }
  return (
    <span className={`ot-flag ot-flag--${state}`} data-kit="flag" style={{ height: logical(row.height), gap: logical(row.gap) }}>
      {box}
      <span className="ot-flag__word" style={{ marginTop: logical(row.wordTop) }}>
        {words}
      </span>
    </span>
  );
}
